import app, { auth } from './firebase';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { resolveServerUrl } from './utils/serverUrl';
import { Capacitor } from '@capacitor/core';
import { initPushNotifications, registerFCMToken } from './utils/pushNotifications';
import { getActiveRoomId } from './utils/activeChat';

const SERVER_URL = resolveServerUrl();
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let fcmInitialized = false;

export async function initializePushNotificationsForUser(currentUser) {
  try {
    if (!currentUser) return;
    if (fcmInitialized) {
      console.log('🔔 Push notifications already initialized, skipping');
      return;
    }
    fcmInitialized = true;

    // Native push notifications (Android/iOS)
    if (Capacitor.isNativePlatform()) {
      console.log('🔔 Initializing native push notifications');
      const userToken = await currentUser.getIdToken();
      
      await initPushNotifications(async (event) => {
        if (event.type === 'registration') {
          console.log('🔔 Native FCM token:', event.token);
          await registerFCMToken(event.token, userToken);
        } else if (event.type === 'foreground') {
          console.log('🔔 Foreground notification:', event.notification);
          // Handle notification display in app
        } else if (event.type === 'action') {
          console.log('🔔 Notification tapped:', event.notification);
          // Handle notification tap action
        }
      });
      return;
    }

    const supported = await isSupported();
    if (!supported) {
      console.warn('Push messaging is not supported in this browser.');
      return;
    }

    if (!('Notification' in window)) {
      console.warn('Notifications are not available in this environment.');
      return;
    }

    if (!VAPID_KEY) {
      console.warn('Missing REACT_APP_FIREBASE_VAPID_KEY; skipping FCM token registration.');
      return;
    }

    // Check current permission state, but do NOT request here (must be user-gesture gated)
    const currentPermission = Notification.permission;
    console.log('🔔 Current notification permission:', currentPermission);

    if (currentPermission !== 'granted') {
      console.warn('🔔 Notification permission is not granted; skipping web push init.');
      return;
    }

    // Reset denial count on successful permission (covers manual enabling in site settings)
    localStorage.removeItem('notification_denied_count');

    const registration = await navigator.serviceWorker.ready;
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    console.log('FCM Token generated:', token);

    if (token) {
      await registerTokenWithServer(token);
    }

    // Foreground messages
    onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      try {
        const title = payload?.notification?.title || payload?.data?.senderName || 'New Message';
        const body = payload?.notification?.body || '';
        const icon = payload?.data?.senderPhoto || '/logo192.png';
        const link = payload?.fcmOptions?.link || payload?.data?.link;
        const roomId = payload?.data?.roomId;

        if (document.visibilityState === 'visible') {
          // Stop notifications while the relevant chat is open
          try {
            const active = getActiveRoomId();
            if (roomId && active && roomId === active) {
              return;
            }
          } catch (_) {}

          // Try showing a basic Notification
          if (Notification.permission === 'granted') {
            const n = new Notification(title, { 
              body, 
              icon, 
              data: { link, roomId } 
            });
            n.onclick = () => {
              try {
                // Prefer current origin. If payload link is an old domain, don't navigate away.
                let base = link || '/';
                if (typeof base === 'string' && /^https?:\/\//i.test(base)) {
                  const parsed = new URL(base);
                  base = `${parsed.pathname || '/'}${parsed.search || ''}${parsed.hash || ''}`;
                }
                const u = new URL(base, window.location.origin);
                if (roomId) u.searchParams.set('openChat', roomId);
                window.location.href = u.toString();
              } catch (_) {
                const fallback = roomId
                  ? `${window.location.origin}/?openChat=${encodeURIComponent(roomId)}`
                  : `${window.location.origin}/`;
                window.location.href = fallback;
              }
              n.close();
            };
          }
        }
      } catch (err) {
        console.warn('Error handling foreground message:', err);
      }
    });
  } catch (error) {
    console.error('Failed to initialize push notifications:', error);
  }
}

// Request web notification permission (must be called from a user gesture)
export async function requestWebPushPermissionWithUserGesture() {
  try {
    const supported = await isSupported();
    if (!supported) return { granted: false, reason: 'unsupported' };
    if (typeof Notification === 'undefined') return { granted: false, reason: 'unsupported' };

    const current = Notification.permission;
    if (current === 'granted') return { granted: true };

    let permission;
    try {
      permission = await Notification.requestPermission();
    } catch (e) {
      return { granted: false, reason: 'blocked' };
    }

    if (permission === 'granted') {
      localStorage.removeItem('notification_denied_count');
      return { granted: true };
    }

    if (permission === 'denied') {
      const deniedCount = parseInt(localStorage.getItem('notification_denied_count') || '0');
      localStorage.setItem('notification_denied_count', (deniedCount + 1).toString());
      return { granted: false, reason: 'denied' };
    }

    return { granted: false, reason: 'dismissed' };
  } catch (error) {
    console.warn('requestWebPushPermissionWithUserGesture failed:', error);
    return { granted: false, reason: 'error' };
  }
}

// Show recovery UI for users who have blocked notifications
function showNotificationRecoveryUI() {
  // Create a modal or banner explaining how to enable notifications
  const recoveryModal = document.createElement('div');
  recoveryModal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  
	// Determine browser to show the right settings link/help
	const ua = navigator.userAgent || '';
	let settingsLink = null;
	let browserName = 'your browser';
	if (/Edg\//.test(ua)) {
		browserName = 'Microsoft Edge';
		settingsLink = 'edge://settings/content/notifications';
	} else if (/Firefox\//.test(ua)) {
		browserName = 'Firefox';
		settingsLink = 'about:preferences#privacy';
	} else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
		browserName = 'Safari';
		settingsLink = null; // Safari requires menu navigation
	} else {
		browserName = 'Chrome or Chromium-based';
		settingsLink = 'chrome://settings/content/notifications';
	}

	const helpListHtml = `
		<ol style="margin: 0 0 20px 0; padding-left: 20px; color: #666; line-height: 1.6;">
			<li>Click the site settings icon (tune or padlock) next to the URL</li>
			<li>Find Notifications and set it to Allow for this site</li>
			<li>Reload this page</li>
		</ol>
	`;

	const copyLinkButton = settingsLink ? `
		<button id="recovery-copy-link" style="
			padding: 8px 16px;
			border: 1px solid #ddd;
			background: white;
			border-radius: 6px;
			cursor: pointer;
			color: #666;
		" title="Copy ${browserName} notification settings link to clipboard">Copy settings link</button>
	` : '';

	recoveryModal.innerHTML = `
		<div style="
			background: white;
			padding: 30px;
			border-radius: 12px;
			max-width: 460px;
			margin: 20px;
			box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
		">
			<h3 style="margin: 0 0 16px 0; color: #333; font-size: 20px;">
				🔔 Enable Notifications
			</h3>
			<p style="margin: 0 0 12px 0; color: #666; line-height: 1.5;">
				Notifications are blocked for this site. To enable them in ${browserName}:
			</p>
			${helpListHtml}
			<p style="margin: 0 0 20px 0; color: #666; line-height: 1.5;">
				${settingsLink ? 'You can also open the notifications settings page directly:' : 'On Safari: Safari → Settings → Websites → Notifications → Allow for this site.'}
			</p>
			<div style="display: flex; gap: 12px; justify-content: flex-end; flex-wrap: wrap;">
				<button id="recovery-dismiss" style="
					padding: 8px 16px;
					border: 1px solid #ddd;
					background: white;
					border-radius: 6px;
					cursor: pointer;
					color: #666;
				">Dismiss</button>
				${copyLinkButton}
				<button id="recovery-refresh" style="
					padding: 8px 16px;
					border: none;
					background: #007bff;
					color: white;
					border-radius: 6px;
					cursor: pointer;
				">Refresh Page</button>
			</div>
		</div>
	`;
  
  document.body.appendChild(recoveryModal);
  
	// Add event listeners
  recoveryModal.querySelector('#recovery-dismiss').onclick = () => {
    document.body.removeChild(recoveryModal);
  };
  
  recoveryModal.querySelector('#recovery-refresh').onclick = () => {
    window.location.reload();
  };

	const copyBtn = recoveryModal.querySelector('#recovery-copy-link');
	if (copyBtn && settingsLink) {
		copyBtn.onclick = async () => {
			try {
				await navigator.clipboard.writeText(settingsLink);
				copyBtn.textContent = 'Copied!';
				setTimeout(() => (copyBtn.textContent = 'Copy settings link'), 2000);
			} catch (_) {}
		};
	}
  
  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (document.body.contains(recoveryModal)) {
      document.body.removeChild(recoveryModal);
    }
  }, 30000);
}

// Check notification permission status and show appropriate UI
export function checkNotificationPermissionStatus() {
  if (typeof Notification === 'undefined') {
    console.log('🔔 Notifications not supported in this browser');
    return;
  }

  const permission = Notification.permission;
  const deniedCount = parseInt(localStorage.getItem('notification_denied_count') || '0');
  
  console.log('🔔 Notification permission status:', permission, 'denied count:', deniedCount);
  
  if (permission === 'denied' && deniedCount >= 3) {
    console.log('🔔 Notifications blocked by browser due to multiple denials');
    // Show a subtle banner or indicator that notifications are blocked
    showNotificationBlockedBanner();
  }
}

// Show a subtle banner when notifications are blocked
function showNotificationBlockedBanner() {
  // Check if banner already exists
  if (document.getElementById('notification-blocked-banner')) {
    return;
  }

  const banner = document.createElement('div');
  banner.id = 'notification-blocked-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #ff6b6b;
    color: white;
    padding: 8px 16px;
    text-align: center;
    font-size: 14px;
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `;
  
	// Build contextual message and quick-help
	const ua = navigator.userAgent || '';
	let browserName = 'your browser';
	if (/Edg\//.test(ua)) browserName = 'Edge';
	else if (/Firefox\//.test(ua)) browserName = 'Firefox';
	else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browserName = 'Safari';
	else browserName = 'Chrome';

	banner.innerHTML = `
		<span>🔔 Notifications are blocked. Use the ${browserName} site settings (tune/padlock icon) to Allow, then reload.</span>
		<button onclick="this.parentElement.remove()" style="
			background: none;
			border: none;
			color: white;
			font-size: 16px;
			cursor: pointer;
			margin-left: 12px;
			padding: 0;
		">×</button>
	`;
  
  document.body.appendChild(banner);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (document.body.contains(banner)) {
      banner.remove();
    }
  }, 10000);
}

// Attach a bottom sticky permission bar if notifications are not granted
export function attachBottomPermissionBarIfNeeded(onGranted) {
	try {
		if (typeof Notification === 'undefined') return;
		const permission = Notification.permission;
		if (permission === 'granted') return;

		if (document.getElementById('notification-permission-bar')) return;

		const bar = document.createElement('div');
		bar.id = 'notification-permission-bar';
		bar.style.cssText = `
			position: fixed;
			left: 16px;
			bottom: 16px;
			background: #1f2937;
			color: white;
			padding: 12px 14px;
			font-size: 14px;
			z-index: 9999;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			cursor: pointer;
			box-shadow: 0 8px 24px rgba(0,0,0,0.25);
			border-radius: 12px;
			max-width: 320px;
			text-align: left;
			line-height: 1.4;
		`;

		bar.innerHTML = `
			<span style="display:block;">🔔 Enable notifications to get message alerts.</span>
			<span style="display:block; opacity:0.85; font-size:13px; margin-top:4px;">Click to allow.</span>
		`;

		bar.onclick = async () => {
			try {
				const result = await requestWebPushPermissionWithUserGesture();
				if (result && result.granted) {
					if (document.body.contains(bar)) bar.remove();
					const blockedBanner = document.getElementById('notification-blocked-banner');
					if (blockedBanner) blockedBanner.remove();
					if (typeof onGranted === 'function') {
						onGranted();
					}
				} else {
					showNotificationRecoveryUI();
				}
			} catch (_) {
				showNotificationRecoveryUI();
			}
		};

		document.body.appendChild(bar);
	} catch (e) {
		console.warn('Failed to attach permission bar:', e);
	}
}

export async function unregisterPushTokenForCurrentUser() {
  try {
    const user = auth.currentUser;
    if (!user) return;
    const supported = await isSupported();
    if (!supported) return;
    const registration = await navigator.serviceWorker.ready;
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    if (!token) return;
    await fetch(`${SERVER_URL}/api/fcm/unregister`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`
      },
      body: JSON.stringify({ token })
    });
  } catch (error) {
    console.warn('Failed to unregister push token:', error);
  }
}

async function registerTokenWithServer(token) {
  const user = auth.currentUser;
  if (!user) return;

  // Skip if this exact token was already registered
  try {
    const cachedToken = localStorage.getItem('tchat_fcm_token');
    if (cachedToken === token) {
      console.log('🔔 FCM token unchanged, skipping registration');
      return;
    }
  } catch (_) {}

  const response = await fetch(`${SERVER_URL}/api/fcm/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ token })
  });
  console.log('Token registration response:', response);

  // Cache the registered token
  try {
    localStorage.setItem('tchat_fcm_token', token);
  } catch (_) {}
}


